import React from 'react';
import { Dropdown, Menu, Tooltip } from 'antd';
import styled from 'styled-components';
import { Theme } from '@routific/hive';

const { Colors } = Theme;

export interface MenuItem {
  key: string;
  button: React.ReactNode;
}

export interface MenuSection {
  key: string;
  title?: string;
  items: MenuItem[];
}

interface Props {
  triggerButton: React.ReactNode;
  menuItems?: MenuItem[];
  menuSections?: MenuSection[];
  tooltipText?: string;
  overlayClassName?: string;
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
  disableDropdown?: boolean;
}

export const StyledMenu = styled(Menu)`
  .action-button-span {
    width: 100%;
  }

  .ant-dropdown-menu-item:hover:not(.ant-dropdown-menu-item-danger) {
    background-color: ${Colors.primary2};
    color: ${Colors.textPrimary};

    &.ant-dropdown-menu-item-disabled:hover {
      background-color: ${Colors.gray0};
      color: ${Colors.textInvalid};
    }
  }

  .ant-dropdown-menu-item {
    &.ant-dropdown-menu-item-danger{
      background-color: ${Colors.gray0};
      color: ${Colors.danger5};

      &.ant-dropdown-menu-item-disabled {
        background-color: ${Colors.gray0};
        color: ${Colors.textInvalid};
      }
    }

    &.ant-dropdown-menu-item-danger:hover {
        background-color: ${Colors.danger2};
        color: ${Colors.danger5};
    }

    &.ant-dropdown-menu-item-disabled:hover {
      background-color: ${Colors.gray0};
      color: ${Colors.textInvalid};
    }
  }
`;

const SectionedMenu = styled(StyledMenu)`
  padding: ${Theme.Spacing.smSpacing} 0;

  .ant-dropdown-menu-item-group-title {
    color: ${Colors.textSecondary};
    font-size: 12px;
    font-weight: 600;
    padding: ${Theme.Spacing.smSpacing} ${Theme.Spacing.mdSpacing};
  }

  .ant-dropdown-menu-item-group-list {
    margin: 0;
    padding: 0;
  }

  .ant-dropdown-menu-item-divider {
    margin: ${Theme.Spacing.smSpacing} 0;
    background-color: ${Colors.gray2};
  }
`;

const ActionDropdown: React.FC<Props> = ({
  triggerButton,
  menuItems,
  menuSections,
  tooltipText,
  overlayClassName,
  placement = 'bottomRight',
  disableDropdown = false,
}) => {
  const menuOverlay = menuSections ? (
    <SectionedMenu>
      {menuSections.flatMap((section: MenuSection, index: number) => {
        const group = (
          <Menu.ItemGroup key={section.key} title={section.title}>
            {section.items.map((item) => item.button)}
          </Menu.ItemGroup>
        );
        return index < menuSections.length - 1
          ? [group, <Menu.Divider key={`${section.key}-divider`} />]
          : [group];
      })}
    </SectionedMenu>
  ) : (
    <StyledMenu>
      {(menuItems ?? []).map((item) => item.button)}
    </StyledMenu>
  );

  const overlayContent = tooltipText ? (
    <Tooltip title={tooltipText}>{menuOverlay}</Tooltip>
  ) : (
    menuOverlay
  );

  return (
    <Dropdown
      overlay={overlayContent}
      trigger={['click']}
      overlayClassName={overlayClassName}
      placement={placement}
      disabled={disableDropdown}
    >
      {triggerButton}
    </Dropdown>
  );
};

export default ActionDropdown;
